import { CITIES, RASPIL_CATEGORY, RASPIL_SUBCATEGORIES } from '@pomoshnik/shared';
import { Header } from '@/components/Header/Header';
import { BottomBar } from '@/components/BottomBar/BottomBar';
import { TextInput } from '@/components/forms/TextInput';
import { Select } from '@/components/forms/Select';

/**
 * Presenter Экрана 2.9.2 «Анкета поставщика» — Блок 9 (ADR-0005, ADR-0086).
 *
 * Чистая функция props → JSX. Никаких useEffect / fetch / useNavigate — всё
 * в Container `CooperationForm.tsx`. Дочерний экран блока → VideoButton НЕ
 * дублируется (P-040). Layout shell (P-046), BottomBar default.
 *
 * Поля — строго по контракту `contracts/cooperation.ts` (имена/обязательность
 * verbatim, P-011):
 *   - customService — только при service==='other' (TextInput);
 *   - serviceSubcategory — только при service===RASPIL_CATEGORY (Select);
 *   - city (Select, required) / companyOrName / address / message (required);
 *   - contactPerson (optional); whatsapp/telegram/max (нужен ≥1, статический хинт).
 *
 * 5 async-состояний (P-021): idle / loading / error (success → navigate, unmount).
 * Дизайн-система: bg-card/text-ink (P-038/P-039), Manrope (P-043), палитра
 * токенами (P-045); жёлтый только заливка submit (P-041 allowlist).
 */

export type CooperationFormSubmitState = 'idle' | 'loading' | 'error';

export interface CooperationFormValues {
  customService: string;
  serviceSubcategory: string;
  city: string;
  companyOrName: string;
  contactPerson: string;
  address: string;
  whatsapp: string;
  telegram: string;
  max: string;
  message: string;
}

export interface CooperationFormViewProps {
  /** Услуга из query (?service=) — определяет условные поля. */
  service: string;
  values: CooperationFormValues;
  submitState: CooperationFormSubmitState;
  errorMessage: string | null;
  onFieldChange: (field: keyof CooperationFormValues, value: string) => void;
  onSubmit: () => void;
}

const MESSAGE_MAX_LENGTH = 2000;

// Город — из общего справочника (§1.3, 20 городов). Соответствует CitySchema контракта.
const CITY_OPTIONS = CITIES.map((c) => ({ value: c, label: c }));

export function CooperationFormView(props: CooperationFormViewProps) {
  const showCustomService = props.service === 'other';
  const showSubcategory = props.service === RASPIL_CATEGORY;
  const isLoading = props.submitState === 'loading';

  return (
    <main className="min-h-screen flex flex-col bg-canvas">
      <Header title="Сотрудничество" />

      <section className="flex-1 p-6 overflow-y-auto flex flex-col gap-5">
        <h1 className="font-display text-h1 text-ink m-0">Анкета поставщика</h1>

        {/* customService — только service==='other' (required в этом случае) */}
        {showCustomService && (
          <TextInput
            name="customService"
            label="Опишите услугу"
            value={props.values.customService}
            onChange={(v) => props.onFieldChange('customService', v)}
            maxLength={200}
            required
          />
        )}

        {/* serviceSubcategory — только service===RASPIL_CATEGORY (распил-подкатегория) */}
        {showSubcategory && (
          <Select
            name="serviceSubcategory"
            label="Подкатегория"
            value={props.values.serviceSubcategory}
            onChange={(v) => props.onFieldChange('serviceSubcategory', v)}
            options={RASPIL_SUBCATEGORIES.map((s) => ({ value: s, label: s }))}
            placeholder="Выберите подкатегорию"
            required
          />
        )}

        <Select
          name="city"
          label="Город"
          value={props.values.city}
          onChange={(v) => props.onFieldChange('city', v)}
          options={CITY_OPTIONS}
          placeholder="Выберите город"
          required
        />

        <TextInput
          name="companyOrName"
          label="Компания или имя"
          value={props.values.companyOrName}
          onChange={(v) => props.onFieldChange('companyOrName', v)}
          maxLength={200}
          required
        />

        <TextInput
          name="contactPerson"
          label="Контактное лицо (необязательно)"
          value={props.values.contactPerson}
          onChange={(v) => props.onFieldChange('contactPerson', v)}
          maxLength={200}
        />

        <TextInput
          name="address"
          label="Адрес"
          value={props.values.address}
          onChange={(v) => props.onFieldChange('address', v)}
          maxLength={300}
          required
        />

        {/* Мессенджеры — нужен ≥1 (статический хинт под группой) */}
        <fieldset className="flex flex-col gap-3 m-0 p-0 border-0">
          <legend className="text-ink font-semibold text-base p-0 mb-1">Контакты</legend>
          <TextInput
            name="whatsapp"
            label="WhatsApp"
            value={props.values.whatsapp}
            onChange={(v) => props.onFieldChange('whatsapp', v)}
            maxLength={100}
          />
          <TextInput
            name="telegram"
            label="Telegram"
            value={props.values.telegram}
            onChange={(v) => props.onFieldChange('telegram', v)}
            maxLength={100}
          />
          <TextInput
            name="max"
            label="Max"
            value={props.values.max}
            onChange={(v) => props.onFieldChange('max', v)}
            maxLength={100}
          />
          <p className="text-muted text-sm mt-1">Укажите хотя бы один</p>
        </fieldset>

        {/* message — нативный <textarea> (TextArea-компоненты нет); className из TextInput */}
        <div className="flex flex-col">
          <label htmlFor="cooperation-message" className="text-ink font-semibold text-base mb-2">
            Сообщение
          </label>
          <textarea
            id="cooperation-message"
            name="message"
            value={props.values.message}
            onChange={(e) => props.onFieldChange('message', e.target.value)}
            maxLength={MESSAGE_MAX_LENGTH}
            required
            rows={5}
            className={
              'min-h-tap rounded-xl bg-card text-ink text-base px-3 py-3 ' +
              'border-[1.5px] border-accent ' +
              'focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none ' +
              'resize-y'
            }
          />
        </div>

        {/* error-состояние (P-021) */}
        {props.submitState === 'error' && props.errorMessage && (
          <div
            role="alert"
            aria-live="polite"
            className="rounded-lg bg-card border-[1.5px] border-error text-error text-sm p-3"
          >
            {props.errorMessage}
          </div>
        )}

        <button
          type="button"
          onClick={props.onSubmit}
          disabled={isLoading}
          aria-busy={isLoading}
          className={
            'w-full min-h-tap rounded-lg bg-cta border-b-2 border-cta-shadow text-ink ' +
            'font-bold text-[18px] py-3 ' +
            'focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none active:opacity-90 ' +
            'disabled:opacity-60 disabled:cursor-not-allowed'
          }
        >
          {isLoading ? 'Отправляем…' : 'Отправить заявку'}
        </button>
      </section>

      <BottomBar />
    </main>
  );
}
